import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { TipOption } from "@/types/orders";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { Heart, Sparkles, Star, Gift } from "lucide-react";

interface TipSelectorProps {
  selectedTip: number;
  onTipChange: (percentage: number) => void;
  subtotal: number;
}

const tipOptions: TipOption[] = [
  {
    percentage: 0,
    label: "0%",
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
  },
  {
    percentage: 2,
    label: "2%",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  {
    percentage: 5,
    label: "5%",
    color: "text-green-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
  },
  {
    percentage: 10,
    label: "10%",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
  },
];

const getTipIcon = (percentage: number) => {
  switch (percentage) {
    case 2:
      return <Heart className="h-3 w-3" />;
    case 5:
      return <Sparkles className="h-3 w-3" />;
    case 10:
      return <Star className="h-3 w-3" />;
    default:
      return null;
  }
};

const getGratitudeMessage = (percentage: number, t: (key: string) => string) => {
  switch (percentage) {
    case 2:
      return t('tipGratitude.thankYou');
    case 5:
      return t('tipGratitude.youreAmazing');
    case 10:
      return t('tipGratitude.youreWonderful');
    default:
      return t('tipGratitude.thankYouSoMuch');
  }
};

export function TipSelector({ selectedTip, onTipChange, subtotal }: TipSelectorProps) {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);

  const handleTipSelect = (percentage: number) => {
    const previousTip = selectedTip;
    onTipChange(percentage);
    
    // Show confetti for non-zero tips
    if (percentage > 0 && previousTip !== percentage) {
      setShowConfetti(true);
      triggerConfetti();
    }
  };

  const triggerConfetti = () => {
    // Create confetti effect
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'],
    });

    // Hide confetti after animation
    setTimeout(() => setShowConfetti(false), 2000);
  };

  const calculateTipAmount = (percentage: number) => {
    return (subtotal * percentage) / 100;
  };

  return (
    <div className="space-y-3 justify-center items-center space-x-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          {t("tipLabel")}
        </label>
        {selectedTip > 0 && (
          <div className="text-sm text-green-600 font-medium animate-pulse">
            +{formatCurrency(calculateTipAmount(selectedTip))}
          </div>
        )}
      </div>
      
      <div className="justify-center grid grid-cols-4 gap-1.5 space-x-2">
        {tipOptions.map((option) => {
          const isSelected = selectedTip === option.percentage;
          const tipAmount = calculateTipAmount(option.percentage);
          
          return (
            <Card
              key={option.percentage}
              className={`
                cursor-pointer transition-all duration-200 transform hover:scale-105
                ${option.bgColor} ${option.borderColor} border-2
                ${isSelected ? 'ring-2 ring-primary ring-offset-1 scale-105' : 'hover:shadow-md'}
                ${option.percentage === 0 ? 'hover:bg-gray-100' : ''}
              `}
              onClick={() => handleTipSelect(option.percentage)}
            >
              <div className="p-2 text-center">
                <div className={`flex items-center justify-center gap-1 mb-1 ${option.color}`}>
                  {getTipIcon(option.percentage)}
                  <span className="font-semibold text-xs">{option.label}</span>
                </div>
                {option.percentage > 0 && (
                  <div className="text-xs text-gray-500">
                    {formatCurrency(tipAmount)}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      
      {selectedTip > 0 && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-pink-100 to-purple-100 rounded-full">
            <Sparkles className="h-3 w-3 text-purple-600" />
            <span className="text-xs font-medium text-purple-700">
              {getGratitudeMessage(selectedTip, t)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
} 